<template>
  <div class="p-4">
    <page-header
      :page="page"
      :actions="pageActionConfig"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />

    <validation-observer ref="remProRequestForm" tag="form" novalidate>
      <SectionTitle content="Detalles del Servicio" has-line />
      <div class="mb-4 p-4 bg-light-gray rounded">
        <div class="evaluation__container">
          <span class="dot-icon">
            <i
              class="far fa-clipboard-list-check"
              style="font-weight: 200;"
            ></i>
          </span>
          <span class="evaluation-title">Evaluación</span>
        </div>
        <div
          class="row mt-3"
          v-for="evaluation in evaluations"
          :key="evaluation.id"
        >
          <div class="col-12 col-md-4">
            <custom-drop-down
              v-model="evaluation.type"
              :clearable="false"
              disabled
              label="Tipo de Evaluación:"
              fieldkey="id"
              fieldname="name"
              :options="[evaluation.type]"
            />
          </div>
          <div class="col-12 col-md-4">
            <custom-drop-down
              v-model="evaluation.discipline"
              :clearable="false"
              disabled
              label="Disciplina:"
              fieldkey="id"
              fieldname="name"
              :options="[evaluation.discipline]"
            />
          </div>
          <div class="col-12 col-md-4">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio-button
                v-model="evaluation.referralOption"
                label="¿Se completó referido a la evaluación recomendada?"
                :name="`rb-${evaluation.id}`"
                :options="referralRadioButtonOptions"
                class="radio-button"
                is-inline
                :error="!!errors[0]"
                custom-error-msg="El valor es requerido"
              />
            </validation-provider>
          </div>
        </div>
      </div>

      <SectionTitle content="Información General de la Discusión" has-line />
      <div class="mb-4 p-4 bg-light-gray rounded">
        <div class="row">
          <div class="col-12 col-md-3">
            <validation-provider
              v-slot="{ errors }"
              name="requestDate"
              rules="required"
            >
              <custom-date-picker
                v-model="model.requestDate"
                label="Fecha de solicitud:"
                :class-container="'w-100'"
                :custom-error-msg="'La fecha de solicitud es requerida.'"
                :error="errors[0]"
                format="dd/MMM./yyyy"
                :max-date="maxDate"
              />
            </validation-provider>
          </div>
          <div class="col-12 col-md-3">
            <div v-if="model.unknownApplicantNameFlag">
              <label class="data-label">Nombre de quien solicita:</label>
              <input class="form-control" disabled />
            </div>
            <validation-provider
              v-else
              v-slot="{ errors }"
              name="applicantNameValue"
              :rules="!model.unknownApplicantNameFlag ? 'required' : ''"
            >
              <CustomInput
                v-model="model.applicantName"
                label="Nombre de quien solicita:"
                type="text"
                :error="errors[0]"
                custom-error-msg="El nombre del solicitante es requerido."
              />
            </validation-provider>
            <check-box
              v-model="model.unknownApplicantNameFlag"
              text="Desconocido"
            />
          </div>
          <div class="col-12 col-md-3">
            <validation-provider
              v-slot="{ errors }"
              name="applicant"
              rules="required"
            >
              <custom-drop-down
                v-model="model.applicantCategory"
                label="Solicitante:"
                placeholder="Seleccione"
                fieldkey="id"
                fieldname="name"
                :options="applicantCategoryOptions"
                :error="errors[0]"
                custom-error-msg="El solicitante es requerido."
              />
            </validation-provider>
          </div>
          <div v-if="!isApplicantFatherMotherOrManager" class="col-12 col-md-3">
            <validation-provider
              v-slot="{ errors }"
              name="personInChargeName"
              rules="required"
            >
              <custom-input
                v-model="model.personInChargeName"
                label="Encargado:"
                :custom-error-msg="'El nombre del encargado es requerido.'"
                :error="errors[0]"
              />
            </validation-provider>
          </div>
        </div>
      </div>

      <SectionTitle content="Información de Contacto" has-line />
      <div class="mb-4 p-4 bg-light-gray rounded">
        <div class="row">
          <div class="col-md">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-input
                v-model="model.contactName"
                label="Nombre:"
                custom-error-msg="El nombre es requerido."
                :error="errors[0]"
              />
            </validation-provider>
          </div>
          <div class="col-md">
            <validation-provider
              v-slot="{ errors }"
              name="email"
              :custom-messages="{
                email: 'El correo electrónico no es válido.',
                required: 'El correo electronico es requerido.',
              }"
              rules="required|email"
            >
              <custom-input
                v-model="model.contactEmail"
                label="Correo Electrónico:"
                :custom-error-msg="errors[0]"
                :error="errors[0]"
                type="email"
              />
            </validation-provider>
          </div>
          <div class="col-md">
            <validation-provider
              v-slot="{ errors }"
              name="cellPhone"
              :rules="!isPhoneFilled ? 'required' : ''"
            >
              <custom-input
                v-model="model.contactCellPhone"
                label="Teléfono Celular:"
                custom-error-msg="El teléfono celular es requerido."
                :error="errors[0]"
                mask-type="Phone"
                placeholder="(___)-___-____"
              />
            </validation-provider>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-4">
            <validation-provider
              v-slot="{ errors }"
              name="homePhone"
              :rules="!isPhoneFilled ? 'required' : ''"
            >
              <custom-input
                v-model="model.contactHomePhone"
                label="Teléfono Hogar:"
                custom-error-msg="El teléfono del hogar es requerido."
                :error="errors[0]"
                mask-type="Phone"
                placeholder="(___)-___-____"
              />
            </validation-provider>
          </div>
          <div class="col-md-4">
            <validation-provider
              v-slot="{ errors }"
              name="workPhone"
              :rules="!isPhoneFilled ? 'required' : ''"
            >
              <custom-input
                v-model="model.contactWorkPhone"
                label="Teléfono Trabajo:"
                custom-error-msg="El teléfono del trabajo es requerido."
                :error="errors[0]"
                mask-type="Phone"
                placeholder="(___)-___-____"
              />
            </validation-provider>
          </div>
          <div class="col-md-2">
            <custom-input
              v-model="model.contactPhoneExtension"
              label="Ext:"
              placeholder="____"
              type="number"
            />
          </div>
        </div>
      </div>

      <SectionTitle
        content="Dirección postal a recibir contestación"
        has-line
      />
      <div class="mb-4 p-4 bg-light-gray rounded">
        <div class="row">
          <div class="col-md">
            <validation-provider
              v-slot="{ errors }"
              name="addressLine1"
              rules="required"
            >
              <custom-input
                v-model="model.addressLine1"
                label="Dirección (Línea 1):"
                custom-error-msg="La dirección es requerida."
                :error="errors[0]"
              />
            </validation-provider>
          </div>
          <div class="col-md">
            <validation-provider
              v-slot="{ errors }"
              name="municipalityPostalAddress"
              rules="required"
            >
              <custom-drop-down
                v-model="model.addressMunicipality"
                label="Munipicio:"
                custom-error-msg="El municipio es requerido."
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                :options="municipalityOptions"
                placeholder="Seleccione"
              />
            </validation-provider>
          </div>
          <div class="col-md">
            <label class="data-label">País:</label>
            <input v-model="model.addressState" class="form-control" disabled />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-4">
            <custom-input
              label="Dirección (Línea 2):"
              v-model="model.addressLine2"
            />
          </div>
          <div class="col-md-4">
            <validation-provider
              v-slot="{ errors }"
              name="postalCode"
              rules="required"
            >
              <custom-input
                v-model="model.addressPostalCode"
                label="Código Postal:"
                custom-error-msg="El código postal es requerido."
                :error="errors[0]"
                type="number"
              />
            </validation-provider>
          </div>
        </div>
      </div>

      <SectionTitle content="Información de Servicio Educativo" has-line />
      <div class="mb-4 p-4 bg-light-gray rounded">
        <div class="row">
          <div class="col-md-4 col-sm-12">
            <validation-provider
              v-slot="{ errors }"
              name="locationTypeEducationalService"
              rules="required"
            >
              <custom-drop-down
                label="Lugar:"
                v-model="model.serviceLocationType"
                :clearable="false"
                custom-error-msg="El lugar es requerido."
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                :options="serviceLocationTypeOptions"
                placeholder="Seleccione"
                @input="onChangePlace"
              />
            </validation-provider>
          </div>

          <div class="col-md">
            <validation-provider
              v-slot="{ errors }"
              name="regionEducationalService"
              rules="required"
            >
              <custom-drop-down
                v-model="model.serviceRegion"
                label="Región:"
                :clearable="false"
                custom-error-msg="La región es requerida."
                :error="errors[0]"
                :disabled="showServiceName || eduServInfoDisabled"
                fieldkey="id"
                fieldname="name"
                :options="serviceRegionOptions"
                placeholder="Seleccione"
                @input="onRegionChange($event)"
              />
            </validation-provider>
          </div>
          <div class="col-md">
            <validation-provider
              v-slot="{ errors }"
              name="districtEducationalService"
              rules="required"
            >
              <custom-drop-down
                v-model="model.serviceDistrict"
                label="Distrito:"
                :clearable="false"
                custom-error-msg="El distrito es requerido."
                :disabled="showServiceName || eduServInfoDisabled"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                :options="serviceDistrictOptions"
                placeholder="Seleccione"
                @input="onDistrictChange($event)"
              />
            </validation-provider>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-4 col-sm-12" v-if="showMunicipality">
            <validation-provider
              v-slot="{ errors }"
              name="municipalityEducationalService"
              rules="required"
            >
              <custom-drop-down
                label="Munipicio:"
                :clearable="false"
                custom-error-msg="El municipio es requerido."
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                :options="serviceMunicipalityOptions"
                :disabled="eduServInfoDisabled"
                placeholder="Seleccione"
                v-model="model.serviceMunicipality"
                @input="onMunicipalityChange($event)"
              />
            </validation-provider>
          </div>

          <div class="col-md">
            <validation-provider
              v-slot="{ errors }"
              name="schoolEducationalService"
              rules="required"
            >
              <custom-drop-down
                v-model="model.serviceSchool"
                label="Escuela:"
                :clearable="false"
                custom-error-msg="La escuela es requerida."
                :disabled="
                  (model.serviceSchool && showServiceName) ||
                    eduServInfoDisabled
                "
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                :options="serviceSchoolOptions"
                placeholder="Seleccione"
              />
            </validation-provider>
          </div>
          <div v-if="showServiceName" class="col-md">
            <validation-provider
              v-slot="{ errors }"
              name="nameEducationalService"
              rules="required"
            >
              <custom-input
                label="Nombre:"
                v-model="model.servicePrivateLocationName"
                :disabled="isPrivateSchoolNameDisabled"
                custom-error-msg="El nombre es requerido."
                :error="errors[0]"
              />
            </validation-provider>
          </div>
        </div>
      </div>

      <SectionTitle
        content="Información adicional a ser considerada"
        has-line
      />
      <validation-provider v-slot="{ errors }" rules="required">
        <custom-text-area
          v-model="model.additionalInformation"
          :count-classes="['float-right']"
          :maxlength="3000"
          show-char-count
          :error="errors[0]"
          custom-error-msg="Favor registrar un comentario."
        />
      </validation-provider>
    </validation-observer>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import CheckBox from 'CheckBox';
import CustomInput from 'CustomInput';
import SectionTitle from 'SectionTitle';
import CustomTextArea from 'CustomTextArea';
import alertMixin from '@/mixin/alert.mixin';
import CustomRadioButton from 'custom-radio';
import CustomDropDown from 'custom-drop-down';
import CustomDatePicker from 'custom-date-picker';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ApplicantCategories from '@/utils/constants/applicantCategories';
import schoolTypes from '@/utils/constants/student/schoolTypes.js';

const RbReferralOptions = [
  {
    label: 'Si',
    value: 1,
  },
  {
    label: 'No',
    value: 2,
  },
  {
    label: 'No Sé',
    value: 3,
  },
];

import {
  getBaseRemProRequestData,
  saveRemProRequest,
} from '@/modules/student-evaluations/api.js';

export default {
  name: 'MainCreateRpRequest',
  components: {
    CheckBox,
    PageHeader,
    CustomInput,
    SectionTitle,
    CustomTextArea,
    CustomDropDown,
    CustomDatePicker,
    CustomRadioButton,
    ValidationObserver,
    ValidationProvider,
  },
  mixins: [alertMixin],
  data: () => ({
    page: {
      icon: 'fas fa-clipboard-list-check',
      title: 'Solicitud de Remedio Provisional',
      routes: ['Inicio', 'Estudiante', 'Solicitud de Remedio Provisional'],
    },
    currentYear: {
      id: null,
      description: null,
      year: null,
    },
    model: {
      requestDate: null,
      applicantName: null,
      unknownApplicantNameFlag: null,
      personInChargeName: null,
      applicantCategory: null,
      /*Contact*/
      contactName: null,
      contactEmail: null,
      contactCellPhone: null,
      contactHomePhone: null,
      contactWorkPhone: null,
      contactPhoneExtension: null,
      /*Address*/
      addressLine1: null,
      addressMunicipality: null,
      addressState: null,
      addressLine2: null,
      addressPostalCode: null,
      /*Service*/
      serviceCenterId: null,
      serviceLocationType: null,
      serviceRegion: null,
      serviceDistrict: null,
      serviceMunicipality: null,
      serviceSchool: null,
      servicePrivateLocationName: null,
      additionalInformation: null,
    },
    eduServInfoDisabled: true,
    maxDate: new Date(),
    evaluations: [],
    referralRadioButtonOptions: [],
    applicantCategoryOptions: [],
    municipalityOptions: [],
    serviceLocationTypeOptions: [],
    serviceDistrictOptions: [],
    serviceRegionOptions: [],
    serviceMunicipalityOptions: [],
    serviceSchoolOptions: [],
    canSave: true,
    servicePrivateLocationName_backup: null,
  }),
  computed: {
    pageActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.goToInbox(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          visible: this.canSave === true,
          click: () => this.createRequest(),
        },
      ];
    },
    isApplicantFatherMotherOrManager() {
      return (
        this.model.applicantCategory?.id ==
        ApplicantCategories.FatherMotherOrManager
      );
    },
    showServiceName() {
      if (!this.model.serviceLocationType?.id) return true;
      const requireServiceName = [
        schoolTypes.PrivateSchool,
        schoolTypes.HeadStart,
        schoolTypes.CareCenter,
      ];
      return requireServiceName.includes(this.model.serviceLocationType.id);
    },
    showMunicipality() {
      if (!this.model.serviceLocationType?.id) return true;
      const requireServiceName = [
        schoolTypes.PrivateSchool,
        schoolTypes.HeadStart,
        schoolTypes.CareCenter,
        schoolTypes.Home,
      ];
      return requireServiceName.includes(this.model.serviceLocationType.id);
    },
    isPhoneFilled() {
      const cellFilled = Boolean(this.model.contactCellPhone);
      const homeFilled = Boolean(this.model.contactHomePhone);
      const workFilled = Boolean(this.model.contactWorkPhone);

      return cellFilled || homeFilled || workFilled;
    },
    payload() {
      return {
        studentSieId: parseInt(this.$route.params.StudentSieId),
        evaluations: this.evaluations.map((x) => ({
          id: x.id,
          typeId: x.type.id,
          disciplineId: x.discipline.id,
          referralOptionId: parseInt(x.referralOption),
        })),
        requestDate: this.model.requestDate,
        applicantName: this.model.applicantName,
        applicantCategoryId: this.model.applicantCategory?.id,
        personInChargeName: this.model.personInChargeName,
        contactName: this.model.contactName,
        contactEmail: this.model.contactEmail,
        contactCellPhone: this.model.contactCellPhone,
        contactHomePhone: this.model.contactHomePhone,
        contactWorkPhone: this.model.contactWorkPhone,
        contactPhoneExtension: this.model.contactPhoneExtension,
        addressLine1: this.model.addressLine1,
        addressMunicipalityId: this.model.addressMunicipality.id,
        addressState: this.model.addressState,
        addressLine2: this.model.addressLine2,
        addressPostalCode: this.model.addressPostalCode,
        serviceCenterId: this.model.serviceCenterId,
        serviceLocationTypeId: this.model.serviceLocationType.id,
        serviceRegionId: this.model.serviceRegion.id,
        serviceDistrictId: this.model.serviceDistrict.id,
        serviceMunicipalityId: this.model.serviceMunicipality.id,
        serviceSchoolId: this.model.serviceSchool.id,
        servicePrivateLocationName: this.model.servicePrivateLocationName,
        additionalInformation: this.model.additionalInformation,
      };
    },
    isPrivateSchoolNameDisabled() {
      return (
        !this.showServiceName ||
        this.servicePrivateLocationName_backup?.length > 0
      );
    },
  },
  watch: {
    'model.unknownApplicantNameFlag'(value) {
      if (!value) return;

      this.model.applicantName = null;
    },
  },
  methods: {
    validateEvaluationsAndRedirectIfFail() {
      if (!!this.$route.query.RejectedEvaluationIds) return;

      this.$router.go(-1);
    },
    goToInbox() {
      if (this.$route.params.From == 'registry') {
        this.$router.push('/student/registrygeneral');
        return;
      }

      this.$router.push({
        name: `MainEvaluationDiscussHistory`,
      });
    },
    async fetchCurrentYear() {
      this.$ApiGet(`api/academicyears/current`).then((response) => {
        this.currentYear = response.data;
      });
    },
    async fetchInitData() {
      const { data } = await getBaseRemProRequestData(
        this.$route.params.StudentSieId,
        this.$route.query.RejectedEvaluationIds
      );

      /*Collections*/
      this.evaluations = data.evaluations.map((x) => ({
        ...x,
        referralOption: '',
      }));
      this.referralRadioButtonOptions = RbReferralOptions;
      this.applicantCategoryOptions = data.applicantCategoryOptions;
      this.municipalityOptions = data.municipalityOptions;
      this.serviceLocationTypeOptions = data.serviceLocationTypeOptions;

      /*General*/
      this.model.requestDate = new Date();
      this.model.applicantName = data.currentUserFullName;
      this.model.applicantCategory = this.applicantCategoryOptions.find(
        (x) => x.id == ApplicantCategories.SAEEFunctionary
      );
      this.model.personInChargeName = data.personInChargeName;

      /*Contact*/
      this.model.contactName = data.contactName;
      this.model.contactEmail = data.contactEmail;
      this.model.contactCellPhone = data.contactCellPhone;
      this.model.contactHomePhone = data.contactHomePhone;
      this.model.contactWorkPhone = data.contactWorkPhone;
      this.model.contactPhoneExtension = data.contactPhoneExtension;

      /*Address*/
      this.model.addressLine1 = data.addressLine1;
      this.model.addressMunicipality = this.municipalityOptions.find(
        (x) => x.id == data.addressMunicipalityId
      );
      this.model.addressState = data.addressState;
      this.model.addressLine2 = data.addressLine2;
      this.model.addressPostalCode = data.addressPostalCode;

      /*Service*/
      this.model.serviceCenterId = data.serviceCenterId;
      this.model.serviceLocationType = data.serviceLocationType;
      this.model.serviceRegion = data.serviceRegion;
      this.model.serviceDistrict = data.serviceDistrict;
      this.model.serviceMunicipality = data.serviceMunicipality;
      this.model.serviceSchool = data.serviceSchool;
      this.model.servicePrivateLocationName = data.servicePrivateLocationName;
    },
    async createRequest() {
      const isValid = await this.$refs.remProRequestForm.validate();
      if (!isValid) {
        this.ShowToast(
          'Error',
          'Favor de completar la información solicitada.',
          'error'
        );
        return;
      }
      try {
        if (!this.canSave) return;

        this.canSave = false;
        await saveRemProRequest(this.payload);
        this.ShowToast(
          '',
          'La solicitud de Remedio Provisional fue creada de manera exitosa.',
          'success'
        );

        setTimeout(() => this.goToInbox(), 1500);
      } catch (err) {
        this.canSave = true;
        this.ShowToast('', 'Error al crear la solicitud.', 'error');
      }
    },
    onChangePlace() {
      this.eduServInfoDisabled = false;
      this.model.serviceMunicipality = null;
      this.model.serviceSchool = null;
      this.serviceSchoolOptions = [];
      this.serviceDistrictOptions = [];
    },
    onMunicipalityChange({ ...item }) {
      this.model.serviceMunicipality = item;
      this.loadRegion(item.id);
    },
    onRegionChange(item) {
      this.model.serviceRegion = item;
      this.model.serviceDistrict = null;
      this.model.serviceSchool = null;
      this.serviceSchoolOptions = [];
      this.loadDistrictsByRegionId(item.id);
    },
    onDistrictChange(item) {
      this.model.serviceDistrict = item;
      this.model.serviceSchool = null;
      this.serviceSchoolOptions = [];
      this.loadSchools(this.model.serviceLocationType.id, item.id, null);
    },
    loadRegionAndMunicipality() {
      this.$ApiGet(
        `api/schools/types/regions?academicyear=${this.currentYear.id}`
      ).then((response) => {
        this.serviceRegionOptions = response.data;
      });

      this.$ApiGet('/api/schools/municipalities').then((res) => {
        this.serviceMunicipalityOptions = res.data;
      });
    },
    loadRegion(municipalityId) {
      this.$ApiGet(
        `api/schools/types/regions?academicyear=${this.currentYear.id}`
      ).then((response) => {
        this.serviceRegionOptions = response.data;
        this.loadDistricts(municipalityId);
      });
    },
    loadDistrictsByRegionId(regionId) {
      this.$ApiGet(
        `api/schools/types/regions/districts?regionid=${regionId}&academicyear=${this.currentYear.id}`
      ).then((response) => {
        this.serviceDistrictOptions = response.data;
      });
    },
    loadDistricts(municipalityId) {
      this.$ApiGet(`api/schools/types/regions/districts`).then((response) => {
        this.serviceDistrictOptions = response.data;
        this.loadRegionAndDistrictByMunicipality(municipalityId);
      });
    },
    loadRegionAndDistrictByMunicipality(municipalityId) {
      this.$ApiGet(
        `api/schools/types/regions/districts/municipality?MunicipalityId=${municipalityId}&academicyear=${this.currentYear.id}`
      ).then((response) => {
        this.model.serviceDistrict = null;
        this.model.serviceRegion = null;
        this.model.serviceRegion = this.serviceRegionOptions.find(
          (x) => x.id == response.data.regionId
        );
        this.model.serviceDistrict = this.serviceDistrictOptions.find(
          (x) => x.id == response.data.districtId
        );

        this.loadSchools(
          this.model.serviceLocationType.id,
          response.data.districtId,
          municipalityId
        );
      });
    },
    loadSchools(schoolType, districtId, municipalityId) {
      this.$ApiGet(
        `api/location/schools?SchoolTypes=${schoolType}&districtid=${districtId}&academicyearid=${this.currentYear.id}&municipalityid=${municipalityId}`
      ).then((response) => {
        this.serviceSchoolOptions = response.data;
        this.model.serviceSchool = null;

        if (this.serviceSchoolOptions.length == 1) {
          this.model.serviceSchool = this.serviceSchoolOptions[0];
        }
      });
    },
  },
  async mounted() {
    this.validateEvaluationsAndRedirectIfFail();
    await this.fetchInitData();
    await this.fetchCurrentYear();
    await this.loadRegionAndMunicipality();
    this.servicePrivateLocationName_backup = this.model.servicePrivateLocationName;
  },
};
</script>

<style scoped>
.evaluation__container {
  padding-bottom: 10px;
  border-bottom: #e1e1e1 solid 1.5px;
}

.evaluation-title {
  color: #3f5b60;
  font-size: 18px;
  margin-left: 20px;
  font-weight: bold;
}

::v-deep .radio-button > .error {
  padding-left: 10px;
}

.dot-icon {
  font-size: 26px;
  width: 40px;
  height: 40px;
  top: 268px;
  border-radius: 50%;
  background: #2db782;
  color: white;
  text-align: center;
  display: inline-block;
}
</style>
